<template>
  <div class="flex flex-col w-full h-full bg-gray-50 overflow-y-auto">
    <div v-if="isLoading" class="flex-1 flex items-center justify-center py-16">
      <loader />
    </div>

    <template v-else-if="resource">
      <div class="flex flex-col px-4 py-4 gap-4 w-full md:max-w-2xl md:mx-auto">
        <!-- Header card -->
        <div class="bg-white border border-gray-200 rounded-2xl p-4 flex items-center gap-3.5">
          <div class="w-14 h-14 rounded-2xl bg-amber-50 text-amber-500 flex items-center justify-center flex-none">
            <i class="pi pi-sun text-2xl" />
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-lg font-bold truncate">{{ resource.name }}</span>
              <span class="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex-none">
                {{ resourceTypeNames[resource.type] }}
              </span>
            </div>
            <div class="text-xs text-gray-500 truncate mt-0.5">
              {{ branchName }} · {{ capacityLabel }}
            </div>
          </div>
        </div>

        <div class="flex gap-2">
          <Button label="עריכת המשאב" icon="pi pi-pencil" outlined size="small" class="flex-1" @click="openEdit" />
          <Button label="מחיקה" icon="pi pi-trash" severity="danger" text size="small" @click="confirmDelete" />
        </div>

        <!-- Details -->
        <section class="rounded-2xl border border-gray-200 bg-white p-4 flex flex-col gap-3">
          <div class="flex items-center gap-2">
            <i class="pi pi-info-circle text-primary" />
            <span class="text-sm font-semibold text-gray-800">פרטי המשאב</span>
          </div>

          <div class="flex items-center justify-between text-sm border-b border-gray-50 py-1.5">
            <span class="text-gray-500">סניף</span>
            <span class="font-medium">{{ branchName }}</span>
          </div>
          <div class="flex items-center justify-between text-sm border-b border-gray-50 py-1.5">
            <span class="text-gray-500">עמדות במקביל</span>
            <span class="font-medium">{{ resource.capacity }}</span>
          </div>
          <div class="flex items-center justify-between text-sm py-1.5">
            <span class="text-gray-500">שעות פעילות</span>
            <span class="font-medium">לפי שעות הסניף</span>
          </div>
        </section>
      </div>
    </template>

    <div v-else class="flex-1 flex items-center justify-center text-sm text-gray-500">המשאב לא נמצא</div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import Button from 'primevue/button';
import { useDrawer } from '@torim-app/shared-components';
import { MAIN_BRANCH_ID, resourceTypeNames } from '@torim-app/types';
import { useAdminResourcesStore, useAdminStore } from '@app/stores';
import { useAdminOnlyPage } from '@app/composables/admin-only-guard';
import { AdminRouteNames } from '@app/router/route-names';
import NewResourceSheet from './NewResourceSheet.vue';

const route = useRoute();
const router = useRouter();
const adminStore = useAdminStore();
useAdminOnlyPage();
const resourcesStore = useAdminResourcesStore();
const toast = useToast();
const { openDrawer, closeDrawer } = useDrawer();

const isLoading = ref(true);

const resourceId = computed(() => route.params.id as string);
const resource = computed(() => resourcesStore.getResources.find((r) => r.id === resourceId.value));

const branchName = computed(() => {
  const branchId = resource.value?.branchId ?? MAIN_BRANCH_ID;
  return adminStore.getBranchSelectOptions.find((b) => b.id === branchId)?.name ?? 'סניף ראשי';
});

const capacityLabel = computed(() =>
  (resource.value?.capacity ?? 1) > 1 ? `${resource.value?.capacity} עמדות` : 'עמדה אחת'
);

const openEdit = () => {
  openDrawer(
    NewResourceSheet,
    { resource: resource.value, onDone: closeDrawer, onClose: closeDrawer },
    { title: 'עריכת משאב' }
  );
};

const confirmDelete = async () => {
  if (!resource.value) return;
  if (!window.confirm(`למחוק את "${resource.value.name}"?`)) return;
  try {
    await resourcesStore.removeResource(resource.value.id);
    toast.success('המשאב נמחק');
    router.push({ name: AdminRouteNames.Team });
  } catch (e) {
    console.error(e);
    toast.error('מחיקה נכשלה');
  }
};

onMounted(async () => {
  if (!resourcesStore.loaded) {
    await resourcesStore.fetchResources().catch(console.error);
  }
  isLoading.value = false;
});
</script>
